import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTRPC } from "@/trpc";
import type { RouterOutputs } from "@/trpc";
import { queryClient } from "@/ajax/queryClient";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Building2, MapPin, DollarSign } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import Loading from "@/components/Loading";
import { formatLocation } from "@/features/job-post/utils/format-location";
import { formatSalary } from "@/features/job-post/utils/format-salary";

type Affiliation =
  RouterOutputs["organizationBusinessUnitAffiliation"]["listForProvider"][number];
type InterestedJob = RouterOutputs["jobPost"]["interested"][number];

function LeaveDialog({
  affiliation,
  open,
  onOpenChange,
}: {
  affiliation: Affiliation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const trpc = useTRPC();

  const removeSelfMutation = useMutation(
    trpc.organizationBusinessUnitAffiliation.removeSelf.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.organizationBusinessUnitAffiliation.listForProvider.queryKey(),
        });
        toast.success("Organization removed from your list.");
        onOpenChange(false);
      },
      onError: (error) => {
        toast.error(error.message || "Failed to remove organization.");
      },
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Remove {affiliation.businessUnitName}?</DialogTitle>
          <DialogDescription>
            This organization will no longer appear in your organizations list.
            Your profile access with this organization will remain unaffected.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={removeSelfMutation.isPending}
            onClick={() =>
              removeSelfMutation.mutate({
                organizationBusinessUnitId:
                  affiliation.organizationBusinessUnitId,
              })
            }
          >
            {removeSelfMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Removing...
              </>
            ) : (
              "Remove"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LikedJobRow({ job }: { job: InterestedJob }) {
  const title = job.title || "Untitled role";

  // Closed jobs: show only the name + a "Closed" indicator, no details.
  if (!job.open) {
    return (
      <li className="flex items-center gap-2 py-2.5">
        <span className="truncate text-sm text-muted-foreground">{title}</span>
        <Badge variant="secondary" className="shrink-0">
          Closed
        </Badge>
      </li>
    );
  }

  const location = formatLocation(job);
  const salary = job.wageFrequencyId ? formatSalary(job) : null;

  return (
    <li className="py-2.5">
      <Link
        to="/provider/jobs/$id"
        params={{ id: job.id }}
        className="text-sm font-medium text-gray-900 transition-colors hover:text-blue-700 hover:underline"
      >
        {title}
      </Link>
      {(location || salary) && (
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {location && (
            <span className="flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {location}
            </span>
          )}
          {salary && (
            <span className="flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              {salary}
            </span>
          )}
        </div>
      )}
    </li>
  );
}

function AffiliationCard({
  affiliation,
  likedJobs,
}: {
  affiliation: Affiliation;
  likedJobs: InterestedJob[];
}) {
  const [dialogOpen, setDialogOpen] = useState(false);

  // Open jobs first, closed jobs sink to the bottom.
  const sortedJobs = useMemo(
    () => [...likedJobs].sort((a, b) => Number(!!b.open) - Number(!!a.open)),
    [likedJobs],
  );

  return (
    <>
      <Card>
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                <h2 className="truncate text-lg font-semibold text-gray-900">
                  {affiliation.organizationName}
                </h2>
              </div>
              <p className="ml-7 mt-0.5 text-sm text-muted-foreground">
                {affiliation.businessUnitName}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setDialogOpen(true)}
            >
              Remove
            </Button>
          </div>

          <div className="mt-4 border-t pt-4">
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Jobs you're interested in
            </h3>
            {sortedJobs.length === 0 ? (
              <p className="py-1 text-sm text-muted-foreground">
                You haven't expressed interest in any of this organization's
                jobs.
              </p>
            ) : (
              <ul className="divide-y">
                {sortedJobs.map((job) => (
                  <LikedJobRow key={job.id} job={job} />
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
      <LeaveDialog
        affiliation={affiliation}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}

function AffiliationsPage() {
  const trpc = useTRPC();

  const affiliationsQuery = useQuery(
    trpc.organizationBusinessUnitAffiliation.listForProvider.queryOptions(),
  );
  const interestedQuery = useQuery(trpc.jobPost.interested.queryOptions());

  // Group the provider's liked jobs by business unit so each organization
  // card can show the jobs the provider expressed interest in with that org.
  const likedJobsByBusinessUnit = useMemo(() => {
    const map = new Map<string, InterestedJob[]>();
    for (const job of interestedQuery.data ?? []) {
      const buId = job.organizationBusinessUnit?.id;
      if (!buId) {
        continue;
      }
      const existing = map.get(buId) ?? [];
      existing.push(job);
      map.set(buId, existing);
    }
    return map;
  }, [interestedQuery.data]);

  if (affiliationsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loading className="h-16 w-16" />
      </div>
    );
  }

  if (affiliationsQuery.error) {
    return (
      <div className="mx-auto max-w-7xl px-4 pt-6">
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            We were unable to load your organizations. Please try again.
          </AlertDescription>
        </Alert>
        <Button onClick={() => affiliationsQuery.refetch()}>Retry</Button>
      </div>
    );
  }

  const affiliations = affiliationsQuery.data ?? [];

  return (
    <div className="mx-auto max-w-7xl px-4 pb-12 pt-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">My Organizations</h1>
        <p className="mt-1 text-muted-foreground">
          Organizations and business units where you are currently hired.
        </p>
      </div>

      {affiliations.length === 0 ? (
        <Card className="mx-auto max-w-2xl">
          <CardContent className="pt-8 pb-8 text-center">
            <Building2 className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">
              You're not hired by any organizations yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {affiliations.map((affiliation) => (
            <AffiliationCard
              key={affiliation.id}
              affiliation={affiliation}
              likedJobs={
                likedJobsByBusinessUnit.get(
                  affiliation.organizationBusinessUnitId,
                ) ?? []
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_provider/provider/affiliations/")({
  component: AffiliationsPage,
});
